import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Newspaper, MessageCircle, Calendar, Tag, AlertTriangle, Star, ChevronRight, X, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  eventos: { label: 'Eventos', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  anuncios: { label: 'Anuncios', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  tips: { label: 'Tips', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  noticias: { label: 'Noticias', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  capacitacion: { label: 'Capacitación', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' }
};

const TAG_STYLES = {
  importante: { icon: Star, color: 'bg-yellow-500 text-black', label: 'IMPORTANTE' },
  urgente: { icon: AlertTriangle, color: 'bg-red-500 text-white', label: 'URGENTE' },
  nuevo: { icon: Tag, color: 'bg-green-500 text-white', label: 'NUEVO' }
};

const BlogSection = ({ token, userType, theme = 'dark', onPostRead }) => {
  const isLight = theme === 'light';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPost = async (postId) => {
    try {
      const response = await axios.get(`${API}/api/blog/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data);
      
      // Actualizar estado de lectura en la lista local
      setPosts(prevPosts => 
        prevPosts.map(p => p.id === postId ? { ...p, is_read: true } : p)
      );
      
      // Notificar al componente padre que se leyó un post
      if (onPostRead) {
        onPostRead();
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Error al cargar el post');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedPost) return;

    setSubmittingComment(true);
    try {
      await axios.post(
        `${API}/api/blog/posts/${selectedPost.id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Recargar el post para ver el nuevo comentario
      const response = await axios.get(`${API}/api/blog/posts/${selectedPost.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data);
      setNewComment('');
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al comentar');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card className={isLight ? "bg-white rounded-2xl shadow-sm border border-gray-100" : "card-viubux-dark border-purple-500/30"}>
        <CardContent className={`pt-6 text-center ${isLight ? 'text-gray-500' : 'text-white/60'}`}>
          Cargando noticias...
        </CardContent>
      </Card>
    );
  }

  if (posts.length === 0) {
    return (
      <Card className={isLight ? "bg-white rounded-2xl shadow-sm border border-gray-100" : "card-viubux-dark border-purple-500/30"}>
        <CardHeader>
          <CardTitle className={`gotham-bold text-xl flex items-center gap-3 ${isLight ? 'text-gray-900' : 'text-white'}`}>
            <Newspaper className="w-5 h-5 text-violet-500" />
            Noticias y Anuncios
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={`text-center py-8 ${isLight ? 'text-gray-500' : 'text-white/60'}`}>
            No hay noticias por el momento. ¡Mantente atento!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={isLight ? "bg-white rounded-2xl shadow-sm border border-gray-100" : "card-viubux-dark border-purple-500/30"} data-testid="blog-section">
        <CardHeader className="pb-2">
          <CardTitle className={`gotham-bold text-xl flex items-center gap-3 ${isLight ? 'text-gray-900' : 'text-white'}`}>
            <Newspaper className="w-5 h-5 text-violet-500" />
            Noticias y Anuncios
            <span className={`text-xs px-2 py-1 rounded-full ml-2 ${isLight ? 'bg-violet-100 text-violet-600' : 'bg-purple-500/20 text-purple-300'}`}>
              {posts.length} {posts.length === 1 ? 'publicación' : 'publicaciones'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {posts.slice(0, 5).map((post) => (
              <div
                key={post.id}
                className={`rounded-xl p-4 transition-colors cursor-pointer border ${
                  isLight 
                    ? 'bg-gray-50 hover:bg-gray-100 border-gray-100 hover:border-violet-200' 
                    : 'bg-black/20 hover:bg-black/30 border-transparent hover:border-purple-500/30'
                }`}
                onClick={() => openPost(post.id)}
                data-testid={`blog-post-${post.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {post.tags?.map((tag) => {
                        const tagStyle = TAG_STYLES[tag];
                        if (!tagStyle) return null;
                        const IconComponent = tagStyle.icon;
                        return (
                          <span
                            key={tag}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${tagStyle.color}`}
                          >
                            <IconComponent className="w-3 h-3" />
                            {tagStyle.label}
                          </span>
                        );
                      })}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${CATEGORY_LABELS[post.category]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                        {CATEGORY_LABELS[post.category]?.label || post.category}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className={`gotham-bold text-lg mb-1 truncate flex items-center gap-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>
                      {!post.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse" title="No leído"></span>
                      )}
                      {post.title}
                    </h3>

                    {/* Summary */}
                    <p className={`verdana-regular text-sm line-clamp-2 ${isLight ? 'text-gray-500' : 'text-white/60'}`}>
                      {post.summary}
                    </p>

                    {/* Meta */}
                    <div className={`flex items-center gap-4 mt-3 text-xs ${isLight ? 'text-gray-400' : 'text-white/40'}`}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {post.comment_count || 0} comentarios
                      </span>
                    </div>
                  </div>

                  <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isLight ? 'text-gray-400' : 'text-white/40'}`} />
                </div>
              </div>
            ))}
          </div>

          {posts.length > 5 && (
            <div className="text-center mt-4">
              <Button variant="outline" className={isLight ? "border-violet-200 text-violet-600 hover:bg-violet-50" : "border-purple-500/50 text-white hover:bg-purple-500/20"}>
                Ver todas las noticias ({posts.length})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Post Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="post-modal">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex items-start justify-between">
                <div>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedPost.tags?.map((tag) => {
                      const tagStyle = TAG_STYLES[tag];
                      if (!tagStyle) return null;
                      const IconComponent = tagStyle.icon;
                      return (
                        <span
                          key={tag}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${tagStyle.color}`}
                        >
                          <IconComponent className="w-3 h-3" />
                          {tagStyle.label}
                        </span>
                      );
                    })}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${CATEGORY_LABELS[selectedPost.category]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                      {CATEGORY_LABELS[selectedPost.category]?.label || selectedPost.category}
                    </span>
                  </div>
                  <h2 className="gotham-bold text-2xl text-white">{selectedPost.title}</h2>
                  <p className="text-white/40 text-sm mt-1">
                    Por {selectedPost.author_name} • {formatDate(selectedPost.created_at)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedPost(null)}
                  className="text-white/60 hover:text-white p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedPost.image_url && (
                <img
                  src={selectedPost.image_url}
                  alt={selectedPost.title}
                  className="w-full h-48 object-cover rounded-lg mb-6"
                />
              )}
              
              <div className="prose prose-invert max-w-none verdana-regular text-white/80 whitespace-pre-wrap">
                {selectedPost.content}
              </div>

              {/* Comments Section */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <h3 className="gotham-bold text-lg text-white mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-purple-400" />
                  Comentarios ({selectedPost.comments?.length || 0})
                </h3>

                {selectedPost.comments_enabled ? (
                  <>
                    {/* Comment Form */}
                    <form onSubmit={handleComment} className="mb-6">
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Escribe un comentario..."
                          className="flex-1 bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                          data-testid="comment-input"
                        />
                        <Button
                          type="submit"
                          disabled={!newComment.trim() || submittingComment}
                          className="btn-viubux"
                          data-testid="submit-comment"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </form>

                    {/* Comments List */}
                    <div className="space-y-4">
                      {selectedPost.comments?.length === 0 ? (
                        <p className="text-white/40 text-center py-4">
                          Sé el primero en comentar
                        </p>
                      ) : (
                        selectedPost.comments?.map((comment) => (
                          <div
                            key={comment.id}
                            className="bg-black/20 rounded-lg p-4"
                            data-testid={`comment-${comment.id}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="gotham-bold text-white text-sm">
                                {comment.user_name}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                comment.user_type === 'author' 
                                  ? 'bg-purple-500/20 text-purple-400' 
                                  : 'bg-green-500/20 text-green-400'
                              }`}>
                                {comment.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                              </span>
                              <span className="text-white/40 text-xs">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="verdana-regular text-white/70 text-sm">
                              {comment.content}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-white/40 text-center py-4 bg-black/20 rounded-lg">
                    Los comentarios están deshabilitados en este post
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogSection;
